import { type Store } from '@ngrx/store';

import {
  type CanvasElementFrom,
  type FormContainerVariantType,
} from '@blaze-runtime/engine-domain';
import { findByRefIds, WrapperField } from '@blaze-runtime/engine-domain';
import {
  ELEMENT_PATH_FORM_FIELD,
  type FormContainerModelField,
  type FormlikeContainerModelField,
  type PropertiesConfigInputVisibilityModel,
} from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';

import { getElementById } from '../../+state/elements/elements.selectors';
import { arePropsWithVisibility } from '../../canvas-elements/element-model/are-props-with-visibility';
import {
  arePropsWithWrapperRefs,
  hasProps,
} from '../../canvas-elements/element-model/element-props-guards';

export function isSelfBindingForm(element: CanvasElementFrom<FormContainerVariantType>): boolean {
  return (
    element.props.content.action === 'insert' || // dragged form allows custom / insert,
    (element.props.content.origin === 'generated' &&
      ['custom', 'view', 'insert', 'update', 'prompt'].includes(element.props.content.action || ''))
  );
}

export function getFormElementIdByModelIdx(
  formFieldPath: string,
  formProps: any,
): { fieldElementId: string | undefined; fieldIdx: number } {
  const fieldIdx = parseInt(
    formFieldPath.split('.')[formFieldPath.startsWith(ELEMENT_PATH_FORM_FIELD) ? 1 : 2],
    10,
  );
  const field = formProps.content.model[fieldIdx] as FormlikeContainerModelField;
  if (!arePropsWithWrapperRefs(formProps)) {
    throw new Error('missing form wrapper refs');
  }
  const fieldElementId = findByRefIds(
    formProps.wrapper.refs,
    WrapperField.getRefIds(field.fieldId, field.field),
  );

  return {
    fieldElementId,
    fieldIdx,
  };
}

export function getFormElementContentVisible(
  formFieldPath: string,
  formProps: any,
  store: Store,
): PropertiesConfigInputVisibilityModel | undefined {
  const { fieldElementId } = getFormElementIdByModelIdx(formFieldPath, formProps);
  if (!fieldElementId) {
    return;
  }

  const fieldElement = syncSelect(store, getElementById(fieldElementId));
  if (!hasProps(fieldElement) || !arePropsWithVisibility(fieldElement.props)) {
    return;
  }

  return fieldElement.props?.content?.visible;
}

export function getFormElementCustomFields(
  model: FormContainerModelField[] | undefined,
): FormContainerModelField[] | undefined {
  return model?.filter((f) => f.isCustom);
}
